/**
 * @description Create a resilient text copy helper for modern browsers and Safari-style fallbacks.
 * @returns {{ copyText: (text: string) => Promise<void> }} Copy helper methods.
 */
export const useCopyText = () => {
  /**
   * @description Copy text through a hidden textarea when the Clipboard API is unavailable.
   * @param {string} text - Text to copy.
   * @returns {void}
   * @throws {Error} Thrown when the legacy copy command fails.
   */
  const fallbackCopyText = (text) => {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', 'readonly')
    textarea.setAttribute('aria-hidden', 'true')
    textarea.style.position = 'fixed'
    textarea.style.top = '0'
    textarea.style.left = '-9999px'
    textarea.style.opacity = '0'

    document.body.appendChild(textarea)
    textarea.focus()
    textarea.select()
    textarea.setSelectionRange(0, textarea.value.length)

    const copied = document.execCommand('copy')
    document.body.removeChild(textarea)

    if (!copied) {
      throw new Error('Fallback copy failed')
    }
  }

  /**
   * @description Copy text with Clipboard API first, then fall back for iOS Safari and non-secure contexts.
   * @param {string} text - Text to copy.
   * @returns {Promise<void>}
   * @throws {Error} Thrown when no valid text is provided or every copy strategy fails.
   */
  const copyText = async (text) => {
    if (!import.meta.client || !text) {
      throw new Error('No text available to copy')
    }

    if (navigator.clipboard?.writeText && window.isSecureContext) {
      try {
        await navigator.clipboard.writeText(text)
        return
      } catch (error) {
        console.warn('Clipboard API copy failed, falling back to execCommand:', error)
      }
    }

    fallbackCopyText(text)
  }

  return {
    copyText,
  }
}
